import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Navigate,
  useBlocker,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  compareOwnersByActiveProperties,
  managementError,
  useManagementResource,
  useWorkgroupEditor,
} from './managementModel';
import './workgroup-management.css';
const ManagementContext = createContext(null);
const useManagement = () => useContext(ManagementContext);
export function WorkgroupManagement({ request, user, chat = false }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = params.get('tab') || (chat ? 'create' : 'workgroups');
  const group = params.get('group');
  const owner = params.get('owner');
  const [pending, setPending] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const guardRef = useRef(null);
  const surface = useRef(null);
  const bypass = useRef(false);
  const scrollPositions = useRef(new Map());
  const routeKey = [tab, group, owner].join(':');
  useLayoutEffect(() => {
    const positions = scrollPositions.current;
    window.scrollTo(0, positions.get(routeKey) || 0);
    const remember = () => positions.set(routeKey, window.scrollY);
    window.addEventListener('scroll', remember, { passive: true });
    return () => {
      window.removeEventListener('scroll', remember);
    };
  }, [routeKey]);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      Boolean(guardRef.current?.dirty || guardRef.current?.saving) &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search),
  );
  const cancelLeave = () => {
    setPending(null);
    if (blocker.state === 'blocked') blocker.reset();
  };
  const finishLeave = () => {
    guardRef.current = null;
    const action = pending;
    setPending(null);
    if (blocker.state === 'blocked') blocker.proceed();
    else action?.();
  };
  const discardAndLeave = () => {
    if (guardRef.current?.saving) return;
    guardRef.current?.reset();
    finishLeave();
  };
  const leave = (action) => {
    if (guardRef.current?.saving) return;
    if (guardRef.current?.dirty) setPending(() => action);
    else action();
  };
  const go = (updates) =>
    leave(() => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(updates)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      setParams(next, {
        replace: !(
          'group' in updates ||
          'owner' in updates ||
          'tab' in updates
        ),
      });
    });
  useEffect(() => {
    const intercept = (event) => {
      if (
        bypass.current ||
        !guardRef.current?.dirty ||
        surface.current?.contains(event.target)
      )
        return;
      const element = event.target.closest('a[href],button[data-leave-page]');
      if (
        !element ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.button !== 0
      )
        return;
      if (
        element instanceof HTMLAnchorElement &&
        (!element.getAttribute('href') ||
          element.getAttribute('href')?.startsWith('#'))
      )
        return;
      event.preventDefault();
      event.stopPropagation();
      if (guardRef.current.saving) return;
      setPending(() => () => {
        bypass.current = true;
        element.click();
        bypass.current = false;
      });
    };
    document.addEventListener('click', intercept, true);
    return () => document.removeEventListener('click', intercept, true);
  }, []);
  async function saveAndLeave() {
    if (guardRef.current?.saving) return;
    setLeaving(true);
    try {
      if (await guardRef.current?.save()) finishLeave();
    } finally {
      setLeaving(false);
    }
  }
  return (
    <ManagementContext.Provider value={{ request, user, guardRef, go }}>
      <section className="wg-management" ref={surface}>
        {!chat && (tab === 'customers' || tab === 'workers') ? (
          <Navigate
            replace
            to={`/chat?${new URLSearchParams({ ...Object.fromEntries(params), view: 'manage' }).toString()}`}
          />
        ) : null}
        <header className="wg-heading">
          <h1>{chat ? 'New conversation' : 'Workgroup management'}</h1>
          {chat ? (
            <button
              className="wg-quiet"
              onClick={() => leave(() => navigate('/chat'))}
            >
              Close
            </button>
          ) : !group ? (
            <button className="wg-primary" onClick={() => go({ group: 'new' })}>
              ＋ New workgroup
            </button>
          ) : null}
        </header>
        {chat &&
        ['ADMIN', 'PROPERTY_MANAGER', 'SUPERVISOR'].includes(
          user.role || '',
        ) ? (
          <nav className="wg-subtabs" aria-label="Create conversation">
            {(user.role === 'ADMIN'
              ? [
                  ['channel', 'New channel'],
                  ['group', 'Group message'],
                  ['direct', 'Direct message'],
                ]
              : [
                  ['group', 'Group message'],
                  ['direct', 'Direct message'],
                ]
            ).map(([value, label]) => (
              <button
                key={value}
                aria-current={
                  (params.get('create') ||
                    (user.role === 'ADMIN' ? 'channel' : 'direct')) === value
                    ? 'page'
                    : undefined
                }
                onClick={() => go({ create: value, tab: 'create', owner: '' })}
              >
                {label}
              </button>
            ))}
            {user.role === 'ADMIN' ? (
              <button
                onClick={() => leave(() => navigate('/settings/workgroups'))}
              >
                Workgroup management ↗
              </button>
            ) : null}
          </nav>
        ) : null}
        {pending || blocker.state === 'blocked' ? (
          <section className="wg-leave" role="alert">
            <strong>Unsaved changes</strong>
            <span>Save your changes before leaving?</span>
            <div className="wg-actions">
              <button
                className="wg-primary"
                disabled={leaving}
                onClick={() => void saveAndLeave()}
              >
                Save and leave
              </button>
              <button disabled={leaving} onClick={discardAndLeave}>
                Discard changes
              </button>
              <button disabled={leaving} onClick={cancelLeave}>
                Continue editing
              </button>
            </div>
          </section>
        ) : null}
        {chat ? (
          <ChatCreation />
        ) : group ? (
          <GroupEditor key={group} id={group === 'new' ? 0 : Number(group)} />
        ) : (
          <GroupList />
        )}
      </section>
    </ManagementContext.Provider>
  );
}
function ResourceStatus({ state, empty, children }) {
  if (state.loading)
    return (
      <div className="wg-status" role="status">
        Loading…
      </div>
    );
  if (state.error)
    return (
      <div className="wg-status wg-error" role="alert">
        {state.error}
        <button onClick={state.reload}>Retry</button>
      </div>
    );
  return empty ? (
    <div className="wg-status">{children || 'No results found.'}</div>
  ) : null;
}
function Pages({ first, next, disabled }) {
  return first || next ? (
    <div className="wg-pagination">
      <button disabled={disabled || !first} onClick={first}>
        First page
      </button>
      <button disabled={disabled || !next} onClick={next}>
        Next page <span aria-hidden="true">→</span>
      </button>
    </div>
  ) : null;
}
function GroupList() {
  const { request, go } = useManagement();
  const [params] = useSearchParams();
  const q = params.get('q') || '',
    filter = params.get('filter') || '',
    after = params.get('after') || '';
  const state = useManagementResource(
    request,
    `/workgroups?limit=50&q=${encodeURIComponent(q)}&filter=${filter}&after_id=${after || 0}`,
  );
  const rows = state.data?.workgroups || [];
  return (
    <section>
      <div className="wg-toolbar">
        <input
          type="search"
          aria-label="Search workgroups"
          placeholder="Search workgroups"
          value={q}
          onChange={(event) => go({ q: event.target.value, after: '' })}
        />
        <select
          aria-label="Workgroup filter"
          value={filter}
          onChange={(event) => go({ filter: event.target.value, after: '' })}
        >
          <option value="">All workgroups</option>
          <option value="mine">My workgroups</option>
          <option value="unstaffed">No staff assigned</option>
        </select>
      </div>
      <ResourceStatus state={state} empty={!rows.length}>
        No workgroups found. Create a workgroup to assign customers and staff.
      </ResourceStatus>
      {rows.length ? (
        <div className="wg-table-scroll">
          <table className="wg-table">
            <thead>
              <tr>
                <th>Workgroup</th>
                <th>Customers</th>
                <th>Active properties</th>
                <th>Responsible staff</th>
                <th>Staffing</th>
                <th>
                  <span className="wg-sr-only">Manage</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} onClick={() => go({ group: String(row.id) })}>
                  <td>
                    <button
                      className="wg-row-link"
                      onClick={(event) => {
                        event.stopPropagation();
                        go({ group: String(row.id) });
                      }}
                    >
                      {row.name}
                    </button>
                  </td>
                  <td>{row.customer_count || 0}</td>
                  <td>{row.active_property_count ?? 'Unavailable'}</td>
                  <td>
                    {row.staff?.map((person) => person.name).join(', ') || '—'}
                  </td>
                  <td>
                    <span
                      className={`wg-badge ${row.staff?.length ? '' : 'is-muted'}`}
                    >
                      {row.staff?.length
                        ? 'Staff assigned'
                        : 'No staff assigned'}
                    </span>
                  </td>
                  <td aria-hidden="true">→</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <Pages
        disabled={state.loading}
        first={after ? () => go({ after: '' }) : undefined}
        next={
          rows.length === 50
            ? () => go({ after: String(rows[rows.length - 1].id) })
            : undefined
        }
      />
    </section>
  );
}
function GroupEditor({ id }) {
  const { request, guardRef, go } = useManagement();
  const editor = useWorkgroupEditor(request, id);
  const [section, setSection] = useState('customers');
  const [countOrder, setCountOrder] = useState('desc');
  const [query, setQuery] = useState(''),
    [after, setAfter] = useState(''),
    [onlySelected, setOnlySelected] = useState(false);
  const directory = useManagementResource(
    request,
    section === 'customers' && !onlySelected
      ? `/management/owners?kind=CUSTOMER&available_only=true&limit=50&q=${encodeURIComponent(query)}&sort=active_property_count&order=${countOrder}&cursor=${encodeURIComponent(after)}`
      : null,
  );
  const updateCustomerCounts = editor.updateCustomerCounts;
  useEffect(() => {
    if (directory.data) updateCustomerCounts(directory.data.owners);
  }, [directory.data, updateCustomerCounts]);
  const save = async () => {
    const value = await editor.save();
    directory.reload();
    if (value && !id) {
      guardRef.current = null;
      go({ group: String(value.id) });
    }
    return value;
  };
  useEffect(() => {
    guardRef.current = {
      dirty: editor.dirty,
      saving: editor.saving,
      save,
      reset: editor.reset,
    };
  });
  useEffect(
    () => () => {
      guardRef.current = null;
    },
    [guardRef],
  );
  const selected = Object.values(editor.customers);
  const rows = onlySelected
    ? selected.filter((row) =>
        `${row.name} ${row.owner_key}`
          .toLowerCase()
          .includes(query.toLowerCase()),
      ).sort((left, right) => compareOwnersByActiveProperties(left, right, countOrder))
    : (directory.data?.owners || []).filter(row => !editor.customers[row.owner_key]);
  const allSelected =
    Boolean(rows.length) &&
    rows.every((row) => editor.customers[row.owner_key]);
  const transfers = selected.filter(
    (row) => row.workgroup_id && row.workgroup_id !== id,
  );
  const staff = editor.people.data?.users || [];
  const staffRows = [
    ...staff,
    ...editor.members
      .filter((value) => !staff.some((row) => row.id === value))
      .map((value) => ({
        id: value,
        name: `Unavailable account #${value}`,
        username: '',
      })),
  ].filter((row) =>
    `${row.name} ${row.username || ''}`
      .toLowerCase()
      .includes(query.toLowerCase()),
  );
  return (
    <section className="wg-editor">
      <button className="wg-back" onClick={() => go({ group: '' })}>
        ← Workgroups
      </button>
      <h2>{id ? editor.savedName || 'Workgroup' : 'New workgroup'}</h2>
      <ResourceStatus state={editor.detail} />
      <ResourceStatus state={editor.people} />
      {editor.ready ? (
        <>
          <label className="wg-field">
            Workgroup name
            <input
              value={editor.name}
              maxLength={120}
              disabled={editor.saving}
              onChange={(event) => editor.setName(event.target.value)}
              placeholder="e.g. East team"
            />
          </label>
          <nav className="wg-subtabs" aria-label="Workgroup details">
            {[
              ['customers', `Customers (${selected.length})`],
              ['team', `Team (${editor.members.length})`],
            ].map(([value, label]) => (
              <button
                key={value}
                aria-current={section === value ? 'page' : undefined}
                onClick={() => {
                  setSection(value);
                  setQuery('');
                  setAfter('');
                }}
              >
                {label}
              </button>
            ))}
          </nav>
          <div className="wg-toolbar">
            <input
              type="search"
              aria-label={
                section === 'customers' ? 'Search customers' : 'Search staff'
              }
              placeholder={
                section === 'customers' ? 'Search customers' : 'Search staff'
              }
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setAfter('');
              }}
            />
            {section === 'customers' ? (
              <>
                <select aria-label="Customer selection filter" value={onlySelected ? 'selected' : 'unassigned'} onChange={(event) => { setOnlySelected(event.target.value === 'selected'); setAfter(''); }}>
                  <option value="unassigned">Unassigned</option>
                  <option value="selected">Selected only</option>
                </select>
                <span className="wg-count">{selected.length} selected · {selected.every(row => typeof row.active_property_count === 'number') ? selected.reduce((sum, row) => sum + row.active_property_count, 0) : 'Unavailable'} active properties</span>
              </>
            ) : (
              <span className="wg-count">{editor.members.length} selected</span>
            )}
          </div>
          {section === 'customers' ? (
            <>
              <ResourceStatus state={directory} empty={!rows.length} />
              <div className="wg-selection-actions">
                <label className="wg-check">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    disabled={!rows.length || editor.saving}
                    onChange={(event) =>
                      editor.selectCustomers(rows, event.target.checked)
                    }
                  />
                  {onlySelected
                    ? 'Select displayed customers'
                    : 'Select this page'}
                </label>
                <button
                  disabled={
                    editor.saving ||
                    !rows.some((row) => editor.customers[row.owner_key])
                  }
                  onClick={() => editor.selectCustomers(rows, false)}
                >
                  Remove displayed selections
                </button>
              </div>
              <div className="wg-table-scroll">
                <table className="wg-table wg-select-table">
                  <thead>
                    <tr>
                      <th>Selected</th>
                      <th>Customer</th>
                      <th>Customer ID</th>
                      <th aria-sort={countOrder === 'desc' ? 'descending' : 'ascending'}>
                        <button
                          type="button"
                          className="wg-count-sort"
                          onClick={() => {
                            setCountOrder((value) => value === 'desc' ? 'asc' : 'desc');
                            setAfter('');
                          }}
                        >
                          Active properties <span aria-hidden="true">{countOrder === 'desc' ? '▼' : '▲'}</span>
                        </button>
                      </th>
                      <th>Current workgroup</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.owner_key}
                        tabIndex={editor.saving ? -1 : 0}
                        aria-selected={Boolean(editor.customers[row.owner_key])}
                        onClick={() => {
                          if (!editor.saving) editor.toggleCustomer(row);
                        }}
                        onKeyDown={(event) => {
                          if (
                            event.target === event.currentTarget &&
                            (event.key === ' ' || event.key === 'Enter')
                          ) {
                            event.preventDefault();
                            if (!editor.saving) editor.toggleCustomer(row);
                          }
                        }}
                      >
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Select ${row.name}`}
                            checked={Boolean(editor.customers[row.owner_key])}
                            disabled={editor.saving}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => editor.toggleCustomer(row)}
                          />
                        </td>
                        <td>
                          <strong>{row.name}</strong>
                        </td>
                        <td>{row.owner_key}</td>
                        <td className="wg-property-count">{row.active_property_count ?? 'Unavailable'}</td>
                        <td>{row.workgroup_name || 'Unassigned'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!onlySelected ? (
                <Pages
                  disabled={directory.loading}
                  first={after ? () => setAfter('') : undefined}
                  next={
                    directory.data?.has_more
                      ? () => setAfter(directory.data.next_cursor)
                      : undefined
                  }
                />
              ) : null}
              {transfers.length ? (
                <section className="wg-transfer">
                  <strong>Customer transfers</strong>
                  {transfers.map((row) => (
                    <div key={row.owner_key}>
                      {row.name}: {row.workgroup_name} →{' '}
                      {editor.name.trim() || 'New workgroup'}
                    </div>
                  ))}
                </section>
              ) : null}
            </>
          ) : (
            <div className="wg-people">
              {staffRows.length ? (
                staffRows.map((row) => (
                  <label className="wg-person" key={row.id}>
                    <input
                      type="checkbox"
                      checked={editor.members.includes(row.id)}
                      disabled={editor.saving}
                      onChange={() => editor.toggleMember(row.id)}
                    />
                    <span className="wg-avatar" aria-hidden="true">
                      {(row.name || row.username || '?')
                        .slice(0, 1)
                        .toUpperCase()}
                    </span>
                    <strong>{row.name || row.username}</strong>
                    <span className="wg-account">{row.username}</span>
                  </label>
                ))
              ) : (
                <div className="wg-status">No staff found.</div>
              )}
            </div>
          )}
        </>
      ) : null}
      {editor.error ? (
        <p className="wg-error" role="alert">
          {editor.error}
        </p>
      ) : null}
      {editor.dirty || !id ? (
        <footer className="wg-savebar">
          <span>{editor.dirty ? 'Unsaved changes' : 'New workgroup'}</span>
          <button disabled={editor.saving} onClick={editor.reset}>
            Cancel
          </button>
          <button
            className="wg-primary"
            disabled={!editor.ready || editor.saving || !editor.name.trim()}
            onClick={() => void save()}
          >
            {editor.saving
              ? 'Saving…'
              : id
                ? 'Save changes'
                : 'Create workgroup'}
          </button>
        </footer>
      ) : null}
    </section>
  );
}
function OwnerWorkspace({ kind, selected }) {
  const { request, go } = useManagement();
  const [params] = useSearchParams();
  const query = params.get(`${kind}_q`) || '',
    after = params.get(`${kind}_after`) || '',
    group = params.get('owner_group') || '';
  const state = useManagementResource(
    request,
    `/management/owners?kind=${kind}&limit=50&q=${encodeURIComponent(query)}&after_key=${encodeURIComponent(after)}${kind === 'CUSTOMER' && group ? `&workgroup_id=${group}` : ''}`,
  );
  const [selectedOwner, setSelectedOwner] = useState();
  const rows = state.data?.owners || [];
  const current =
    rows.find((row) => row.owner_key === selected) ||
    (selectedOwner?.owner_key === selected ? selectedOwner : undefined);
  return (
    <div className={`wg-owner-layout ${selected ? 'has-selection' : ''}`}>
      <aside className="wg-owner-list">
        <div className="wg-toolbar">
          <input
            type="search"
            aria-label={`Search ${kind === 'CUSTOMER' ? 'customers' : 'workers'}`}
            placeholder={`Search ${kind === 'CUSTOMER' ? 'customers' : 'workers'}`}
            value={query}
            onChange={(event) =>
              go({ [`${kind}_q`]: event.target.value, [`${kind}_after`]: '' })
            }
          />
        </div>
        {kind === 'CUSTOMER' ? (
          <GroupSelect
            value={group}
            all
            onChange={(value) =>
              go({ owner_group: value, [`${kind}_after`]: '' })
            }
          />
        ) : null}
        <ResourceStatus state={state} empty={!rows.length} />
        <ul>
          {rows.map((row) => (
            <li key={row.owner_key}>
              <button
                aria-current={selected === row.owner_key ? 'page' : undefined}
                onClick={() => {
                  setSelectedOwner(row);
                  go({ owner: row.owner_key });
                }}
              >
                <strong>{row.name}</strong>
                <span className="wg-secondary">
                  {kind === 'CUSTOMER'
                    ? row.workgroup_name || 'Unassigned'
                    : row.owner_key}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <Pages
          disabled={state.loading}
          first={after ? () => go({ [`${kind}_after`]: '' }) : undefined}
          next={
            state.data?.has_more
              ? () => go({ [`${kind}_after`]: state.data.next_cursor })
              : undefined
          }
        />
      </aside>
      <section className="wg-owner-detail">
        {selected ? (
          <OwnerDetail
            key={`${kind}:${selected}`}
            kind={kind}
            ownerKey={selected}
            name={current?.name}
            onChanged={state.reload}
          />
        ) : (
          <div className="wg-empty-detail">
            Select a {kind === 'CUSTOMER' ? 'customer' : 'worker'} to manage
            channels and members.
          </div>
        )}
      </section>
    </div>
  );
}
function GroupSelect({ value, onChange, all = false, disabled = false }) {
  const { request } = useManagement();
  const [groups, setGroups] = useState([]),
    [error, setError] = useState(''),
    [retry, setRetry] = useState(0);
  useEffect(() => {
    const abort = new AbortController();
    setError('');
    void (async () => {
      const rows = [];
      let after = 0;
      while (!abort.signal.aborted) {
        const page = await request(
          `/workgroups?limit=50&after_id=${after}`,
          'GET',
          undefined,
          abort.signal,
        );
        rows.push(...page.workgroups);
        if (page.workgroups.length < 50) break;
        after = page.workgroups[page.workgroups.length - 1].id;
      }
      if (!abort.signal.aborted) setGroups(rows);
    })().catch((cause) => {
      if (!abort.signal.aborted) setError(managementError(cause));
    });
    return () => abort.abort();
  }, [request, retry]);
  return (
    <>
      <select
        aria-label="Workgroup"
        value={value}
        disabled={disabled || Boolean(error)}
        onChange={(event) => onChange(event.target.value)}
      >
        {all ? <option value="">All workgroups</option> : null}
        <option value="0">Unassigned</option>
        {groups.map((group) => (
          <option value={group.id} key={group.id}>
            {group.name}
          </option>
        ))}
      </select>
      {error ? (
        <p role="alert" className="wg-error">
          {error}
          <button onClick={() => setRetry((value) => value + 1)}>Retry</button>
        </p>
      ) : null}
    </>
  );
}
function OwnerDetail({ kind, ownerKey, name, onChanged }) {
  const { request, go } = useManagement();
  const [space, setSpace] = useState(),
    [error, setError] = useState(''),
    [retry, setRetry] = useState(0);
  const [ownerName, setOwnerName] = useState(name || '');
  const [section, setSection] = useState('channels'),
    [creating, setCreating] = useState(false),
    [editing, setEditing] = useState();
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setError('');
    void request('/spaces/ensure', 'POST', {
      kind,
      owner_key: ownerKey,
    })
      .then((result) => {
        if (active) {
          setSpace(result.space);
          if (result.owner) setOwnerName(result.owner.name);
        }
      })
      .catch((cause) => {
        if (active) setError(managementError(cause));
      });
    return () => {
      active = false;
    };
  }, [request, kind, ownerKey, retry]);
  return (
    <>
      <button className="wg-back" onClick={() => go({ owner: '' })}>
        ← {kind === 'CUSTOMER' ? 'Customers' : 'Workers'}
      </button>
      <h2>{ownerName || name || ownerKey}</h2>
      {error ? (
        <p role="alert" className="wg-error">
          {error}
          <button onClick={() => setRetry((value) => value + 1)}>Retry</button>
        </p>
      ) : !space ? (
        <p role="status">Loading…</p>
      ) : creating || editing ? (
        <ChannelEditor
          spaceId={space.id}
          customer={kind === 'CUSTOMER'}
          defaultPrivate={section === 'private channels'}
          editing={editing}
          onDone={() => {
            setCreating(false);
            setEditing(undefined);
            setRevision((value) => value + 1);
          }}
        />
      ) : (
        <>
          <nav className="wg-subtabs" aria-label="Owner settings">
            {[
              'channels',
              'members',
              'settings',
              ...(kind === 'CUSTOMER' ? ['private channels'] : []),
            ].map((value) => (
              <button
                key={value}
                aria-current={section === value ? 'page' : undefined}
                onClick={() => setSection(value)}
              >
                {value[0].toUpperCase() + value.slice(1)}
              </button>
            ))}
          </nav>
          {section === 'members' ? (
            <OwnerMembers spaceId={space.id} />
          ) : section === 'settings' ? (
            <OwnerSettings
              spaceId={space.id}
              customer={kind === 'CUSTOMER'}
              onChanged={onChanged}
            />
          ) : (
            <ChannelList
              key={`${section}:${revision}`}
              spaceId={space.id}
              privateList={section === 'private channels'}
              onCreate={() => setCreating(true)}
              onEdit={setEditing}
            />
          )}
        </>
      )}
    </>
  );
}
function ChannelList({ spaceId, privateList, onCreate, onEdit }) {
  const { request } = useManagement();
  const navigate = useNavigate();
  const [after, setAfter] = useState(0);
  const state = useManagementResource(
    request,
    `/spaces/${spaceId}/${privateList ? 'private-channels' : 'channels'}?limit=50&after_id=${after}`,
  );
  const rows = state.data?.channels || state.data?.conversations || [];
  return (
    <>
      <div className="wg-toolbar">
        <span className="wg-count">
          {privateList
            ? 'Customer staff only · messages are private'
            : 'Channels'}
        </span>
        <button className="wg-primary" onClick={onCreate}>
          ＋ New channel
        </button>
      </div>
      <ResourceStatus state={state} empty={!rows.length}>
        No channels yet.
      </ResourceStatus>
      <ul className="wg-channel-list">
        {rows.map((row) => (
          <li key={row.id}>
            <span>
              <strong>{row.name || `Channel ${row.id}`}</strong>
              {row.archived_at ? (
                <span className="wg-badge is-muted">Archived</span>
              ) : null}
            </span>
            <button
              onClick={() =>
                privateList ? onEdit(row) : navigate(`/chat/${row.id}`)
              }
            >
              {privateList ? 'Manage' : 'Open channel'} →
            </button>
          </li>
        ))}
      </ul>
      <Pages
        first={after ? () => setAfter(0) : undefined}
        next={
          rows.length === 50
            ? () => setAfter(rows[rows.length - 1].id)
            : undefined
        }
      />
    </>
  );
}
function OwnerMembers({ spaceId }) {
  const { request } = useManagement();
  const [query, setQuery] = useState(''),
    [after, setAfter] = useState(0),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const state = useManagementResource(
    request,
    `/spaces/${spaceId}/options?limit=50&q=${encodeURIComponent(query)}&after_id=${after}`,
  );
  const rows = state.data?.users || [];
  const change = async (row) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await request(
        `/spaces/${spaceId}/members/${row.id}`,
        row.in_space ? 'DELETE' : 'PUT',
      );
      state.reload();
    } catch (cause) {
      setError(managementError(cause));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="wg-toolbar">
        <input
          type="search"
          aria-label="Search members"
          placeholder="Search members"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setAfter(0);
          }}
        />
      </div>
      <ResourceStatus state={state} empty={!rows.length} />
      {error ? (
        <p role="alert" className="wg-error">
          {error}
        </p>
      ) : null}
      <ul className="wg-channel-list">
        {rows.map((row) => (
          <li key={row.id}>
            <span>
              <strong>{row.name || row.username}</strong>
              <span className="wg-secondary">{row.role}</span>
            </span>
            <button disabled={busy} onClick={() => void change(row)}>
              {row.in_space ? 'Remove' : 'Add member'}
            </button>
          </li>
        ))}
      </ul>
      <Pages
        first={after ? () => setAfter(0) : undefined}
        next={
          rows.length === 50
            ? () => setAfter(rows[rows.length - 1].id)
            : undefined
        }
      />
    </>
  );
}
function OwnerSettings({ spaceId, customer, onChanged }) {
  const { request } = useManagement();
  const assignment = useManagementResource(
    request,
    `/spaces/${spaceId}/assignment`,
  );
  const language = useManagementResource(
    request,
    customer ? `/spaces/${spaceId}/language` : null,
  );
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const save = async (path, body) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await request(path, 'PUT', body);
      assignment.reload();
      language.reload();
      onChanged();
    } catch (cause) {
      setError(managementError(cause));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="wg-settings">
      <ResourceStatus state={assignment} />
      {assignment.data ? (
        <label className="wg-field">
          Workgroup
          <GroupSelect
            value={String(assignment.data.workgroup_id || 0)}
            disabled={busy}
            onChange={(value) =>
              void save(`/spaces/${spaceId}/assignment`, {
                workgroup_id: Number(value) || null,
              })
            }
          />
        </label>
      ) : null}
      {customer ? (
        <>
          <ResourceStatus state={language} />
          {language.data ? (
            <label className="wg-field">
              Customer language
              <select
                disabled={busy}
                value={language.data.language}
                onChange={(event) =>
                  void save(`/spaces/${spaceId}/language`, {
                    language: event.target.value,
                  })
                }
              >
                <option value="en">English</option>
                <option value="zh">Chinese</option>
              </select>
            </label>
          ) : null}
        </>
      ) : null}
      {error ? (
        <p role="alert" className="wg-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}
function ChannelEditor({
  spaceId,
  customer,
  defaultPrivate = false,
  editing,
  onDone,
}) {
  const { request, user, guardRef } = useManagement();
  const [name, setName] = useState(editing?.name || ''),
    [privateChannel, setPrivate] = useState(Boolean(editing) || defaultPrivate),
    [archived, setArchived] = useState(Boolean(editing?.archived_at));
  const [members, setMembers] = useState(
      editing?.member_user_ids || (defaultPrivate ? [] : [user.id]),
    ),
    [property, setProperty] = useState();
  const [query, setQuery] = useState(''),
    [after, setAfter] = useState(0),
    [propertyQuery, setPropertyQuery] = useState(''),
    [propertyAfter, setPropertyAfter] = useState(0);
  const [saving, setSaving] = useState(false),
    [error, setError] = useState('');
  const lock = useRef(false);
  const people = useManagementResource(
    request,
    `/spaces/${spaceId}/options?limit=50&q=${encodeURIComponent(query)}&after_id=${after}`,
  );
  const properties = useManagementResource(
    request,
    customer && !editing
      ? `/spaces/${spaceId}/properties?limit=50&q=${encodeURIComponent(propertyQuery)}&after_id=${propertyAfter}`
      : null,
  );
  const rows = people.data?.users || [],
    homes = properties.data?.properties || [];
  const dirty =
    name !== (editing?.name || '') ||
    privateChannel !== (Boolean(editing) || defaultPrivate) ||
    archived !== Boolean(editing?.archived_at) ||
    Boolean(property) ||
    JSON.stringify([...members].sort()) !==
      JSON.stringify(
        [
          ...(editing?.member_user_ids || (defaultPrivate ? [] : [user.id])),
        ].sort(),
      );
  const save = async () => {
    if (lock.current || !name.trim() || !members.length) return undefined;
    lock.current = true;
    setSaving(true);
    setError('');
    try {
      await request(
        editing
          ? `/spaces/${spaceId}/private-channels/${editing.id}`
          : `/spaces/${spaceId}/channels`,
        editing ? 'PUT' : 'POST',
        {
          name: name.trim(),
          member_user_ids: members,
          ...(editing
            ? { archived }
            : { private: privateChannel, property_id: property?.id }),
        },
      );
      guardRef.current = null;
      onDone();
      return true;
    } catch (cause) {
      setError(managementError(cause));
      return undefined;
    } finally {
      lock.current = false;
      setSaving(false);
    }
  };
  const reset = () => {
    setName(editing?.name || '');
    setPrivate(Boolean(editing) || defaultPrivate);
    setArchived(Boolean(editing?.archived_at));
    setMembers(editing?.member_user_ids || (defaultPrivate ? [] : [user.id]));
    setProperty(undefined);
  };
  useEffect(() => {
    guardRef.current = { dirty, saving, save, reset };
  });
  useEffect(
    () => () => {
      guardRef.current = null;
    },
    [guardRef],
  );
  return (
    <div className="wg-editor">
      <h3>{editing ? 'Edit private channel' : 'New channel'}</h3>
      <fieldset disabled={saving}>
        <label className="wg-field">
          Channel name
          <input
            value={name}
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        {customer && !editing ? (
          <label className="wg-check">
            <input
              type="checkbox"
              checked={privateChannel}
              onChange={(event) => {
                setPrivate(event.target.checked);
                setMembers(event.target.checked ? [] : [user.id]);
              }}
            />
            Customer internal channel · Urban cannot read or join
          </label>
        ) : null}
        {editing ? (
          <label className="wg-check">
            <input
              type="checkbox"
              checked={archived}
              onChange={(event) => setArchived(event.target.checked)}
            />
            Archived
          </label>
        ) : null}
        {customer && !editing ? (
          <section className="wg-property-picker">
            <h4>
              Property <span className="wg-secondary">Optional</span>
            </h4>
            {property ? (
              <div className="wg-selection-actions">
                <strong>{property.full_address}</strong>
                <button onClick={() => setProperty(undefined)}>Clear</button>
              </div>
            ) : null}
            <input
              type="search"
              aria-label="Search properties"
              placeholder="Search properties"
              value={propertyQuery}
              onChange={(event) => {
                setPropertyQuery(event.target.value);
                setPropertyAfter(0);
              }}
            />
            <ResourceStatus state={properties} />
            <ul className="wg-property-list">
              {homes.map((home) => (
                <li key={home.id}>
                  <label className="wg-check">
                    <input
                      type="radio"
                      name="channel-property"
                      checked={property?.id === home.id}
                      onChange={() => setProperty(home)}
                    />
                    {home.full_address}
                  </label>
                </li>
              ))}
            </ul>
            <Pages
              first={propertyAfter ? () => setPropertyAfter(0) : undefined}
              next={
                homes.length === 50
                  ? () => setPropertyAfter(homes[homes.length - 1].id)
                  : undefined
              }
            />
          </section>
        ) : null}
        <h4>
          Members <span className="wg-count">{members.length} selected</span>
        </h4>
        <input
          type="search"
          aria-label="Search channel members"
          placeholder="Search members"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setAfter(0);
          }}
        />
        <ResourceStatus state={people} />
        <div className="wg-people">
          {rows
            .filter(
              (row) =>
                !privateChannel ||
                ['AGENT', 'PROPERTY_MANAGER'].includes(
                  row.role?.toUpperCase() || '',
                ),
            )
            .map((row) => (
              <label className="wg-person" key={row.id}>
                <input
                  type="checkbox"
                  checked={members.includes(row.id)}
                  disabled={
                    !row.in_space &&
                    row.role?.toUpperCase() !== 'ADMIN' &&
                    !members.includes(row.id)
                  }
                  onChange={() =>
                    setMembers((current) =>
                      current.includes(row.id)
                        ? current.filter((id) => id !== row.id)
                        : [...current, row.id],
                    )
                  }
                />
                <span>
                  <strong>{row.name}</strong>
                  <span className="wg-secondary">
                    {!row.in_space && row.role?.toUpperCase() !== 'ADMIN'
                      ? 'Add this person in Members first'
                      : row.role}
                  </span>
                </span>
              </label>
            ))}
        </div>
        <Pages
          first={after ? () => setAfter(0) : undefined}
          next={
            rows.length === 50
              ? () => setAfter(rows[rows.length - 1].id)
              : undefined
          }
        />
      </fieldset>
      {error ? (
        <p role="alert" className="wg-error">
          {error}
        </p>
      ) : null}
      <footer className="wg-savebar">
        <span>{members.length} members selected</span>
        <button
          disabled={saving}
          onClick={() => {
            reset();
            guardRef.current = null;
            onDone();
          }}
        >
          Cancel
        </button>
        <button
          className="wg-primary"
          disabled={saving || !name.trim() || !members.length}
          onClick={() => void save()}
        >
          {saving ? 'Saving…' : 'Save channel'}
        </button>
      </footer>
    </div>
  );
}
function ChatCreation() {
  const { user, go } = useManagement();
  const [params] = useSearchParams();
  const type =
    params.get('create') || (user.role === 'ADMIN' ? 'channel' : 'direct');
  const tab = params.get('tab') || 'create';
  const ownerKind = tab === 'workers' ? 'WORKER' : 'CUSTOMER';
  if (!['ADMIN', 'PROPERTY_MANAGER', 'SUPERVISOR'].includes(user.role || ''))
    return <p role="alert">Your account cannot start conversations.</p>;
  if (
    user.role !== 'ADMIN' &&
    (type === 'channel' || tab === 'customers' || tab === 'workers')
  )
    return (
      <p role="alert">Channel management is available to administrators.</p>
    );
  return (
    <>
      {type === 'channel' ? (
        <div className="wg-toolbar">
          <label className="wg-check">
            Channel owner{' '}
            <select
              aria-label="Channel owner"
              value={tab === 'customers' || tab === 'workers' ? tab : 'urban'}
              onChange={(event) => go({ tab: event.target.value, owner: '' })}
            >
              <option value="urban">Urban internal</option>
              <option value="customers">Customer</option>
              <option value="workers">Worker</option>
            </select>
          </label>
        </div>
      ) : null}
      {type === 'channel' && (tab === 'customers' || tab === 'workers') ? (
        <OwnerWorkspace
          key={tab}
          kind={ownerKind}
          selected={params.get('owner') || ''}
        />
      ) : (
        <ConversationCreator
          key={type}
          kind={
            type === 'channel'
              ? 'CHANNEL'
              : type === 'group'
                ? 'GROUP_DM'
                : 'DIRECT'
          }
        />
      )}
    </>
  );
}
function ConversationCreator({ kind }) {
  const { request, user, guardRef } = useManagement();
  const navigate = useNavigate();
  const [name, setName] = useState(''),
    [query, setQuery] = useState(''),
    [cursor, setCursor] = useState('');
  const [selected, setSelected] = useState([]),
    [saving, setSaving] = useState(false),
    [error, setError] = useState('');
  const people = useManagementResource(
    request,
    `/users?${kind === 'CHANNEL' ? '' : 'scope=collaboration&'}limit=50&q=${encodeURIComponent(query)}&cursor=${encodeURIComponent(cursor)}`,
  );
  const valid =
    kind === 'CHANNEL'
      ? Boolean(name.trim())
      : selected.length >= (kind === 'DIRECT' ? 1 : 2);
  const reset = () => {
    setName('');
    setSelected([]);
  };
  const lock = useRef(false);
  const save = async (open = true) => {
    if (!valid || lock.current) return;
    lock.current = true;
    setSaving(true);
    setError('');
    try {
      const result = await request('/conversations', 'POST', {
        kind,
        name: name.trim() || undefined,
        member_user_ids: [user.id, ...selected],
        access_policy_code: 'ADMIN_MANAGED',
      });
      guardRef.current = null;
      if (open) navigate(`/chat/${result.conversation.id}`);
      return true;
    } catch (cause) {
      setError(managementError(cause));
      return false;
    } finally {
      lock.current = false;
      setSaving(false);
    }
  };
  useEffect(() => {
    guardRef.current = {
      dirty: Boolean(name || selected.length),
      saving,
      save: () => save(false),
      reset,
    };
  });
  useEffect(
    () => () => {
      guardRef.current = null;
    },
    [guardRef],
  );
  return (
    <section className="wg-editor">
      {kind !== 'DIRECT' ? (
        <label className="wg-field">
          {kind === 'CHANNEL' ? 'Channel name' : 'Group name (optional)'}
          <input
            value={name}
            maxLength={200}
            disabled={saving}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
      ) : null}
      <div className="wg-toolbar">
        <input
          type="search"
          aria-label="Search people"
          placeholder="Search people"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setCursor('');
          }}
        />
        <span>{selected.length} selected</span>
      </div>
      <ResourceStatus state={people} empty={!people.data?.users.length} />
      <div className="wg-people">
        {people.data?.users
          .filter(
            (person) =>
              person.id !== user.id &&
              (kind !== 'CHANNEL' ||
                `${person.name} ${person.username}`
                  .toLowerCase()
                  .includes(query.toLowerCase())),
          )
          .map((person) => (
            <label className="wg-person" key={person.id}>
              <input
                type={kind === 'DIRECT' ? 'radio' : 'checkbox'}
                name="members"
                disabled={saving}
                checked={selected.includes(person.id)}
                onChange={() =>
                  setSelected((current) =>
                    kind === 'DIRECT'
                      ? [person.id]
                      : current.includes(person.id)
                        ? current.filter((id) => id !== person.id)
                        : [...current, person.id],
                  )
                }
              />
              <strong>{person.name || person.username}</strong>
              <span className="wg-account">{person.username}</span>
            </label>
          ))}
      </div>
      <Pages
        first={cursor ? () => setCursor('') : undefined}
        next={
          people.data?.has_more
            ? () => setCursor(people.data.next_cursor || '')
            : undefined
        }
        disabled={people.loading}
      />
      {error ? (
        <p className="wg-error" role="alert">
          {error}
        </p>
      ) : null}
      <footer className="wg-savebar">
        <span>{selected.length} selected</span>
        <button disabled={saving} onClick={reset}>
          Cancel
        </button>
        <button
          className="wg-primary"
          disabled={saving || !valid}
          onClick={() => void save()}
        >
          {saving ? 'Creating…' : 'Create'}
        </button>
      </footer>
    </section>
  );
}
